// Update product-card images/links to match selected model + default Tipo de Case.
(function () {
  const STAMP_VERSION = "v5";

  function norm(s) {
    return String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
  }

  function deps() {
    return window.MorakVariantResolver;
  }

  function isFilteredCollectionPage() {
    if (!location.pathname.startsWith("/collections/")) return false;
    return !!new URL(location.href).searchParams.get("filter.v.option.modelo");
  }

  function imageKey(src) {
    if (!src) return "";
    try {
      const path = new URL(src, location.origin).pathname;
      return path.split("/").pop()?.split("?")[0] || "";
    } catch {
      return String(src).split("/").pop()?.split("?")[0] || "";
    }
  }

  function getCards(root = document) {
    return [...root.querySelectorAll("product-card[data-product-handle][data-product-url]")];
  }

  function cardImageEl(card) {
    return card.querySelector(".card-gallery img, .product-card__image img, img");
  }

  function variantImageSrc(variant) {
    return (
      variant?.featured_image?.src ||
      variant?.featured_media?.preview_image?.src ||
      ""
    );
  }

  function imageAlreadyMatches(img, nextSrc) {
    if (!img || !nextSrc) return true;
    const current = img.currentSrc || img.src || "";
    return current === nextSrc || imageKey(current) === imageKey(nextSrc);
  }

  function preloadAndSwapImage(img, nextSrc) {
    return new Promise((resolve) => {
      if (!img || !nextSrc || imageAlreadyMatches(img, nextSrc)) return resolve();

      const pre = new Image();
      pre.onload = () => {
        img.src = nextSrc;
        img.removeAttribute("srcset");
        resolve();
      };
      pre.onerror = () => resolve();
      pre.src = nextSrc;
    });
  }

  function setCardLink(card, variantId, baseUrl) {
    const R = deps();
    const href = R
      ? R.applyVariantToUrl(baseUrl, variantId)
      : `${baseUrl}?variant=${variantId}`;

    card.querySelectorAll('a[href*="/products/"]').forEach((a) => {
      a.href = href;
    });
  }

  async function setCardImage(card, variant) {
    const nextSrc = variantImageSrc(variant);
    if (!nextSrc) return;

    const imgs = card.querySelectorAll(".card-gallery img, .product-card__image img, img");
    await Promise.all([...imgs].map((img) => preloadAndSwapImage(img, nextSrc)));

    const cardLink = card.closest("product-card-link");
    if (cardLink) {
      cardLink.setAttribute("data-featured-media-url", nextSrc);
    }
  }

  async function processCard(card, stamp, modelLabel) {
    const R = deps();
    if (!R || !modelLabel) return;

    const applied = card.getAttribute("data-morak-model-applied");
    if (applied === stamp) return;

    const handle = card.getAttribute("data-product-handle");
    const baseUrl = card.getAttribute("data-product-url") || "";
    if (!handle || !baseUrl) return;

    const product = await R.fetchProduct(handle);
    const variant = R.findBestVariant(product, modelLabel);
    if (!variant) return;

    const img = cardImageEl(card);
    const nextSrc = variantImageSrc(variant);
    const needsImage = img && nextSrc && !imageAlreadyMatches(img, nextSrc);
    const needsLink = String(variant.id) !== card.getAttribute("data-morak-model-variant");

    if (!needsImage && !needsLink) {
      card.setAttribute("data-morak-model-applied", stamp);
      card.setAttribute("data-morak-model-variant", String(variant.id));
      return;
    }

    if (needsLink) setCardLink(card, variant.id, baseUrl);
    if (needsImage) await setCardImage(card, variant);

    card.setAttribute("data-morak-model-applied", stamp);
    card.setAttribute("data-morak-model-variant", String(variant.id));
  }

  let applying = false;
  let queuedScope = null;

  async function apply(scope) {
    const R = deps();
    if (!R) return;
    if (applying) {
      queuedScope = scope || queuedScope;
      return;
    }

    const model = R.getSelectedModelLabel();
    if (!model) return;

    applying = true;
    const stamp = `${STAMP_VERSION}|${norm(model)}`;
    const root = scope && scope.querySelectorAll ? scope : document;

    try {
      const cards = getCards(root);
      const pending = cards.filter((card) => card.getAttribute("data-morak-model-applied") !== stamp);
      if (!pending.length) return;

      const batchSize = isFilteredCollectionPage() ? 6 : 12;
      for (let i = 0; i < pending.length; i += batchSize) {
        const batch = pending.slice(i, i + batchSize);
        await Promise.all(batch.map((card) => processCard(card, stamp, model)));
        if (i + batchSize < pending.length) {
          await new Promise((resolve) => requestAnimationFrame(resolve));
        }
      }
    } finally {
      applying = false;
      if (queuedScope) {
        const next = queuedScope;
        queuedScope = null;
        apply(next);
      }
    }
  }

  function watchDom() {
    const grid =
      document.querySelector("results-list [ref='grid']") ||
      document.querySelector("results-list .product-grid") ||
      document.querySelector(".product-grid");

    if (!grid) return;

    let timer = null;
    const obs = new MutationObserver((mutations) => {
      let hasNewCards = false;

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (
            node.matches?.("product-card, li.product-grid__item") ||
            node.querySelector?.("product-card")
          ) {
            hasNewCards = true;
            break;
          }
        }
        if (hasNewCards) break;
      }

      if (!hasNewCards) return;

      clearTimeout(timer);
      timer = setTimeout(() => apply(grid), 50);
    });

    obs.observe(grid, { childList: true, subtree: true });
  }

  function bindModelChange() {
    document.addEventListener(
      "change",
      (e) => {
        if (e.target?.id === "global-model-selector") apply();
      },
      true
    );

    window.addEventListener("storage", (e) => {
      if (e.key === "selected_model") apply();
    });

    document.addEventListener("filter:update", () => apply());
    window.addEventListener("pageshow", () => apply());
  }

  function init() {
    if (!deps()) return;
    apply();
    watchDom();
    bindModelChange();
  }

  window.MorakModelCards = { apply };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
